import { Link } from '@tanstack/react-router';
import {
  ArrowRight,
  Building2,
  CreditCard,
  Activity,
  TrendingUp,
  Users as UsersIcon,
  Server,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
} from 'lucide-react';
import { Button } from '../../components/Button';
import { Card, CardHeader } from '../../components/Card';
import { Money } from '../../components/Money';
import { PageHeader } from '../../components/PageHeader';
import { cn } from '../../lib/cn';
import { downloadCsv } from '../../lib/csv';
import { formatInt, timeAgo } from '../../lib/format';
import {
  usePlatformKpis,
  usePlatformActivity,
  useTopResellers,
  useBusinessesByStatus,
  useSystemHealth,
} from '../../api/admin';

const STATUS_TONES: Record<string, string> = {
  active: 'bg-emerald-500',
  trial: 'bg-amber-500',
  past_due: 'bg-rose-500',
  suspended: 'bg-zinc-500',
  canceled: 'bg-zinc-400',
};

const STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  trial: 'Trial',
  past_due: 'Past due',
  suspended: 'Suspended',
  canceled: 'Canceled',
};

/** Platform admin home — KPIs, live activity, top resellers, business status mix. */
export function AdminOverview() {
  const { data: platformKpis } = usePlatformKpis();
  const { data: platformActivity = [] } = usePlatformActivity();
  const { data: topResellers = [] } = useTopResellers();
  const { data: businessesByStatus = [] } = useBusinessesByStatus();
  const { data: health } = useSystemHealth();

  const totalBiz = businessesByStatus.reduce((s, x) => s + x.count, 0);

  function exportOverviewCsv() {
    if (!platformKpis) return;
    downloadCsv(
      `platform-overview-${new Date().toISOString().slice(0, 10)}`,
      ['metric', 'value'],
      [
        ['resellers', platformKpis.resellers],
        ['businesses', platformKpis.businesses],
        ['businesses_new_this_week', platformKpis.businessesThisWeek ?? ''],
        ['users', platformKpis.users],
        ['mrr_minor_bdt', platformKpis.mrrMinor],
        ['past_due_businesses', platformKpis.pastDueBusinesses ?? ''],
        ['pending_reseller_applications', platformKpis.pendingApplications ?? ''],
        ...businessesByStatus.map((s) => [`businesses_${s.status}`, s.count] as [string, number]),
      ],
    );
  }

  if (!platformKpis) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-[1440px] mx-auto space-y-4" aria-busy="true">
        <div className="skeleton h-8 w-64 max-w-full rounded" />
        <div className="space-y-3">
          <div className="skeleton h-14 rounded-lg" />
          <div className="skeleton h-14 rounded-lg" />
          <div className="skeleton h-14 rounded-lg" />
          <div className="skeleton h-14 rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <PageHeader
        eyebrow="Platform"
        title="Overview"
        subtitle="Resellers, businesses, revenue, and uptime — at a glance."
        action={
          <>
            <Button variant="secondary" size="sm" onClick={exportOverviewCsv}>Export</Button>
            <Link to="/admin/resellers">
              <Button size="sm" iconLeft={<UsersIcon className="w-4 h-4" />}>Invite reseller</Button>
            </Link>
          </>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-6">
        <KpiTile
          icon={<UsersIcon className="w-4 h-4" />}
          label="Resellers"
          value={<span className="tabular-nums text-zinc-950 dark:text-zinc-50">{platformKpis.resellers}</span>}
          subtitle={
            platformKpis.pendingApplications !== undefined
              ? `${platformKpis.pendingApplications} pending application${platformKpis.pendingApplications === 1 ? '' : 's'}`
              : 'partner organizations'
          }
        />
        <KpiTile
          icon={<Building2 className="w-4 h-4" />}
          label="Businesses"
          value={<span className="tabular-nums text-zinc-950 dark:text-zinc-50">{formatInt(platformKpis.businesses)}</span>}
          subtitle={
            platformKpis.businessesThisWeek !== undefined
              ? `+${platformKpis.businessesThisWeek} this week`
              : 'all statuses'
          }
        />
        <KpiTile
          icon={<UsersIcon className="w-4 h-4" />}
          label="Users"
          value={<span className="tabular-nums text-zinc-950 dark:text-zinc-50">{formatInt(platformKpis.users)}</span>}
          subtitle="all roles"
        />
        <KpiTile
          icon={<CreditCard className="w-4 h-4" />}
          label="MRR"
          value={
            <Money
              minor={platformKpis.mrrMinor}
              hideCents
              tone="neutral"
              className="text-zinc-950 dark:text-zinc-50"
            />
          }
          subtitle="across all plans"
        />
        <KpiTile
          icon={<Activity className="w-4 h-4" />}
          label="Past due"
          value={
            <span
              className={cn(
                'tabular-nums',
                (platformKpis.pastDueBusinesses ?? 0) > 0
                  ? 'text-amber-700 dark:text-amber-300'
                  : 'text-zinc-950 dark:text-zinc-50',
              )}
            >
              {platformKpis.pastDueBusinesses ?? 0}
            </span>
          }
          subtitle="businesses past due / suspended"
          // Same set the tile counts (businesses.status in past_due/suspended).
          to="/admin/businesses"
          search={{ status: 'needs_attention' }}
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-3 sm:gap-4 mb-6">
        <Card className="lg:col-span-2 p-5 sm:p-6">
          <CardHeader
            title="Live activity"
            subtitle="Platform events as they happen"
            action={
              <Link to="/admin/audit" className="text-xs font-medium text-brand dark:text-brand-dm hover:underline inline-flex items-center gap-1">
                View audit log <ArrowRight className="w-3 h-3" />
              </Link>
            }
          />
          <ul className="space-y-3">
            {platformActivity.map((a) => (
              <li key={a.id} className="flex items-start gap-3">
                <span className={cn(
                  'w-7 h-7 grid place-items-center rounded-full flex-shrink-0 mt-0.5',
                  a.status === 'ok'
                    ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300'
                    : a.status === 'warn'
                    ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300'
                    : 'bg-rose-50 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300',
                )}>
                  {a.status === 'ok' ? <CheckCircle className="w-3.5 h-3.5" /> : a.status === 'warn' ? <AlertTriangle className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-zinc-900 dark:text-zinc-100 truncate">
                    <span className="font-medium">{a.event}</span>
                    <span className="text-zinc-600 dark:text-zinc-300"> — {a.detail}</span>
                  </div>
                  <div className="text-xs text-zinc-500 dark:text-zinc-400 tabular-nums mt-0.5">
                    {timeAgo(a.at)}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </Card>

        <Card className="p-5 sm:p-6">
          <CardHeader
            title="System health"
            subtitle={health ? (health.allNominal ? 'All services nominal' : 'Attention needed') : 'Checking…'}
            action={
              <Link to="/admin/health" className="text-xs font-medium text-brand dark:text-brand-dm hover:underline">
                Details
              </Link>
            }
          />
          <ul className="space-y-2.5">
            {(health?.services ?? []).map((svc) => (
              <li key={svc.name} className="flex items-center justify-between text-sm gap-2">
                <span className="flex items-center gap-2 min-w-0">
                  <Server className="w-3.5 h-3.5 text-zinc-400 dark:text-zinc-500 flex-shrink-0" />
                  <span className="text-zinc-700 dark:text-zinc-200 truncate">{svc.name}</span>
                </span>
                <span className="flex items-center gap-1.5 text-xs tabular-nums flex-shrink-0">
                  <span
                    className={cn(
                      'w-1.5 h-1.5 rounded-full',
                      svc.status === 'ok'
                        ? 'bg-emerald-500'
                        : svc.status === 'degraded'
                        ? 'bg-amber-500'
                        : svc.status === 'down'
                        ? 'bg-rose-500'
                        : 'bg-zinc-400',
                    )}
                  />
                  <span className="text-zinc-500 dark:text-zinc-400">{svc.detail}</span>
                </span>
              </li>
            ))}
            {!health && (
              <li className="text-xs text-zinc-500 dark:text-zinc-400">Running live checks…</li>
            )}
          </ul>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-3 sm:gap-4">
        <Card className="p-5 sm:p-6">
          <CardHeader
            title="Top resellers"
            subtitle="Ranked by MRR"
            action={
              <Link to="/admin/resellers" className="text-xs font-medium text-brand dark:text-brand-dm hover:underline">
                All resellers
              </Link>
            }
          />
          <ol className="space-y-3">
            {topResellers.map((r, i) => (
              <li key={r.id} className="flex items-center gap-3">
                <span className="w-6 h-6 grid place-items-center rounded-md text-xs font-semibold tabular-nums bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200">
                  {i + 1}
                </span>
                <Link
                  to="/admin/resellers/$id"
                  params={{ id: r.id }}
                  className="flex-1 min-w-0 text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate hover:text-brand dark:hover:text-brand-dm"
                >
                  {r.name}
                </Link>
                <Money minor={r.mrrMinor} hideCents tone="neutral" className="text-sm font-medium text-zinc-950 dark:text-zinc-50" />
              </li>
            ))}
          </ol>
        </Card>

        <Card className="p-5 sm:p-6">
          <CardHeader
            title="Businesses by status"
            subtitle={`${formatInt(totalBiz)} total`}
            action={
              <Link to="/admin/businesses" className="text-xs font-medium text-brand dark:text-brand-dm hover:underline">
                All businesses
              </Link>
            }
          />
          <div className="space-y-2.5">
            {businessesByStatus.map((b) => {
              const pct = (b.count / totalBiz) * 100;
              return (
                <div key={b.status}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="flex items-center gap-2 text-zinc-700 dark:text-zinc-200">
                      <span className={cn('w-1.5 h-1.5 rounded-full', STATUS_TONES[b.status])} />
                      {STATUS_LABELS[b.status]}
                    </span>
                    <span className="text-xs tabular-nums text-zinc-500 dark:text-zinc-400">
                      {b.count} <span className="opacity-60">({pct.toFixed(1)}%)</span>
                    </span>
                  </div>
                  <div className="h-1.5 bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden">
                    <div
                      className={cn('h-full rounded-full', STATUS_TONES[b.status])}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}

/**
 * A KPI tile. When `to` is given the whole tile becomes a link to the list the
 * number came from — a count you cannot click is a dead end, which is what was
 * reported for "Past due" on 2026-07-30.
 *
 * Any destination must resolve to the SAME set the tile counted, or the list will
 * contradict the number and read as a bug.
 */
function KpiTile({
  icon,
  label,
  value,
  subtitle,
  to,
  search,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  subtitle?: string;
  to?: string;
  search?: Record<string, string>;
}) {
  const body = (
    <>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
          {label}
        </div>
        <span className="text-zinc-400 dark:text-zinc-500">{icon}</span>
      </div>
      <div className="text-2xl sm:text-[26px] font-semibold leading-tight">{value}</div>
      {subtitle && (
        <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{subtitle}</div>
      )}
    </>
  );

  if (!to) return <Card className="p-4 sm:p-5">{body}</Card>;

  return (
    <Link to={to} search={search} className="block rounded-xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand">
      <Card className="p-4 sm:p-5 h-full transition-colors hover:border-zinc-300 dark:hover:border-zinc-600 hover:bg-zinc-50/60 dark:hover:bg-zinc-800/40">
        {body}
      </Card>
    </Link>
  );
}
